/**
 * EmailViewModal — Detailed view of a single email.
 * Extracted from DepartmentEmailModule.js for clarity.
 */
import React from 'react';
import { motion } from 'framer-motion';
import {
  Star, X, Paperclip, Reply, Forward, Archive, Trash2,
} from 'lucide-react';

export default function EmailViewModal({
  email,
  gradientClass,
  isRead,
  isStarred,
  onClose,
  onToggleStar,
}) {
  if (!email) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 20 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`bg-gradient-to-r ${gradientClass} p-4 sm:p-5 text-white flex-shrink-0`}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-lg sm:text-xl leading-tight">
                {email.subject || '(No subject)'}
              </h3>
              <p className="text-white/70 text-sm mt-1">
                {email.date ? new Date(email.date).toLocaleString('en-GB') : ''}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={(e) => onToggleStar(email.id, e)}
                className="p-2 rounded-lg hover:bg-white/20 transition-colors"
              >
                {isStarred ? (
                  <Star className="w-5 h-5 text-amber-300 fill-amber-300" />
                ) : (
                  <Star className="w-5 h-5 text-white/50" />
                )}
              </button>
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-white/20 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Email details */}
        <div className="p-4 sm:p-5 border-b border-slate-100 bg-slate-50 flex-shrink-0">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white font-bold">
                {(email.from_name || email.from || 'U')[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-800">
                  {email.from_name || email.from || 'Unknown sender'}
                </p>
                <p className="text-sm text-slate-500 truncate">
                  {email.from || ''}
                </p>
              </div>
              {!isRead && (
                <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                  New
                </span>
              )}
            </div>

            {email.to && (
              <div className="flex items-center gap-2 text-sm text-slate-600 pl-13">
                <span className="text-slate-400">To:</span>
                <span>{email.to}</span>
              </div>
            )}

            {email.has_attachments && (
              <div className="flex items-center gap-2 text-sm bg-amber-50 p-2 rounded-lg border border-amber-200">
                <Paperclip className="w-4 h-4 text-amber-600" />
                <span className="text-amber-700 font-medium">
                  {email.attachments?.length || 1} attachment(s)
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Email body - scrollable */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="prose prose-sm sm:prose max-w-none">
            {email.body_html ? (
              <div
                dangerouslySetInnerHTML={{ __html: email.body_html }}
                className="email-content"
              />
            ) : (
              <pre className="whitespace-pre-wrap text-slate-700 font-sans text-sm sm:text-base leading-relaxed bg-transparent p-0 m-0">
                {email.body_text || email.body || email.snippet || 'No content'}
              </pre>
            )}
          </div>
        </div>

        {/* Actions footer */}
        <div className="p-4 border-t border-slate-100 bg-white flex-shrink-0">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <button className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors text-sm font-medium">
                <Reply className="w-4 h-4" />
                Reply
              </button>
              <button className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors text-sm font-medium">
                <Forward className="w-4 h-4" />
                Forward
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button className="p-2 hover:bg-slate-100 rounded-lg transition-colors text-slate-500">
                <Archive className="w-4 h-4" />
              </button>
              <button className="p-2 hover:bg-red-50 rounded-lg transition-colors text-red-500">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
